/* WP-10 collection draft selection (§14.7) + F1 submission intent (§18.8):
 * draft and pending-request intent are namespaced by the server-issued
 * opaque ownerScope from GET /auth/session, so account/company/env switches
 * on the same browser can never read or replay another owner's state. The
 * legacy unscoped draft key is never migrated — it is discarded on the next
 * authenticated load rather than attributed to whichever account signs in.
 *
 * F1.1 storage policy:
 * - No ownerScope ⇒ fail-closed: draft/intent live in memory only. An old-API
 *   or partial-deployment window must never silently reinstate cross-account
 *   unscoped persistence.
 * - Every intent also writes a tiny key stub: if the payload is ever corrupt,
 *   the stub still yields the key as a probe-only recovery handle.
 * - Age never deletes an unresolved intent — a stale handle stays resolvable
 *   through the authoritative intent-status endpoint; it is cleared only on a
 *   settled verdict, a committed resolution, or confirmed owner sign-out.
 *
 * F1.2 storage policy:
 * - A successful persistent write drops the memory fallback copy — a stale
 *   fallback must never shadow the authoritative local record once storage
 *   recovers (reads return what a reload would see).
 * - the durable layer is read directly (memory bypassed) so the submit path
 *   can prove a recovery handle survives this tab before dispatching; the
 *   page blocks submission when it does not.
 *
 * F1.3 dispatch binding:
 * - persistedIntentBinding(expected) proves the durable record belongs to the
 *   exact intent being dispatched — same owner namespace, same key, same
 *   canonical body. Presence alone is never dispatch authorization: a
 *   different or unidentifiable durable record is a conflict that must be
 *   reconciled through the authoritative flow, never silently overwritten.
 * - Every read/write helper accepts an explicit owner scope so a suspended
 *   continuation can never write, clear or mark whichever namespace happens
 *   to be globally active after an async boundary (step-up, retry, late
 *   response). Callers re-verify the captured owner before every mutation. */

import type { SubmitPickupRequestBody } from '../api/types';

const LEGACY_DRAFT_KEY = 'hp.collectDraft.v1';
const DRAFT_BASE = 'hp.collectDraft.v1';
const INTENT_BASE = 'hp.collectIntent.v1';
const INTENT_STUB_SUFFIX = '.stub';

export type CollectDraft = {
  boxRefs: string[];
  loose: Record<string, number>; // productId -> sticks
};

/** Persisted submission intent: one stable idempotency key bound to the
 * immutable request body, created before the first POST and replayed across
 * retry, navigation, reload and step-up renewal (F1/R03).
 * `body: null` marks a degraded handle: only the key stub survived, so the
 * intent can be probed and settled but never replayed (the body is unknown). */
export type CollectIntent = {
  key: string;
  body: SubmitPickupRequestBody | null;
  state: 'submitting' | 'uncertain';
  createdAt: string; // ISO
  /** Last time a POST attempt was dispatched — the quiesce signal that tells
   * an ABSENT probe apart from an attempt still in transit. */
  lastAttemptAt?: string;
};

const EMPTY: CollectDraft = { boxRefs: [], loose: {} };

/* ---- owner scope -------------------------------------------------------- */

let currentScope: string | null = null;
/** In-memory fallbacks so private-mode/storage-disabled browsers still get
 * same-tab protection without silently reintroducing duplicate submits. */
const memStore = new Map<string, string>();

/** Called by SessionProvider when the session contract resolves. */
export function setPortalOwnerScope(scope: string | null): void {
  currentScope = scope || null;
}

export function getPortalOwnerScope(): string | null {
  return currentScope;
}

/** Fail-closed scopeless namespace: without a server-issued scope there is no
 * safe persistent key, so a reserved pseudo-suffix marks the key memory-only —
 * payload and stub stay distinct and nothing is ever written to real storage. */
const NOSCOPE = '__noscope__';

function scopedKeyFor(base: string, scope: string | null): string {
  return scope ? `${base}.${scope}` : `${base}.${NOSCOPE}`;
}

function isScopeless(key: string): boolean {
  // Segment match — the stub suffix (".stub") must not mask the marker, and a
  // server-issued HMAC scope can never contain the reserved segment.
  return key.split('.').includes(NOSCOPE);
}

/** Discard the legacy unscoped draft once an authenticated session resolves —
 * it can never be attributed to an owner safely. Only runs when a real
 * ownerScope exists; on an older API the unscoped key is left untouched (it
 * is never read either — fail-closed keeps it inert). */
export function discardLegacyDraft(): void {
  if (!currentScope) return;
  try {
    sessionStorage.removeItem(LEGACY_DRAFT_KEY);
  } catch {
    /* ignore */
  }
  memStore.delete(LEGACY_DRAFT_KEY);
}

/** Clear this owner's draft + pending intent on confirmed logout/logout-all. */
export function clearPortalOwnerState(): void {
  if (currentScope) {
    removeStored(sessionStorageOf(), `${DRAFT_BASE}.${currentScope}`);
    removeStored(localStorageOf(), `${INTENT_BASE}.${currentScope}`);
    removeStored(localStorageOf(), `${INTENT_BASE}.${currentScope}${INTENT_STUB_SUFFIX}`);
  }
  discardLegacyDraft();
  memStore.clear();
  currentScope = null;
}

/* ---- storage helpers ---------------------------------------------------- */

function sessionStorageOf(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function localStorageOf(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readStored(storage: Storage | null, key: string): string | null {
  if (isScopeless(key) || memStore.has(key)) return memStore.get(key) ?? null;
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(storage: Storage | null, key: string, value: string): boolean {
  if (isScopeless(key)) {
    // No owner scope — fail-closed: memory only, never persisted unscoped.
    memStore.set(key, value);
    return false;
  }
  if (storage) {
    try {
      storage.setItem(key, value);
      // Persisted data is authoritative again — drop the fallback copy so a
      // stale memory entry can never shadow this write (F1.2-C).
      memStore.delete(key);
      return true;
    } catch {
      /* fall through to memory */
    }
  }
  memStore.set(key, value);
  return false;
}

function removeStored(storage: Storage | null, key: string): void {
  memStore.delete(key);
  if (isScopeless(key) || !storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/* ---- draft -------------------------------------------------------------- */

export function loadDraft(scope: string | null = currentScope): CollectDraft {
  try {
    const raw = readStored(sessionStorageOf(), scopedKeyFor(DRAFT_BASE, scope));
    if (!raw) return { ...EMPTY, loose: {} };
    const parsed = JSON.parse(raw) as Partial<CollectDraft>;
    return {
      boxRefs: Array.isArray(parsed.boxRefs)
        ? parsed.boxRefs.filter((b) => typeof b === 'string')
        : [],
      loose:
        parsed.loose && typeof parsed.loose === 'object'
          ? Object.fromEntries(
              Object.entries(parsed.loose).filter(
                ([, v]) => typeof v === 'number' && Number.isFinite(v) && v > 0,
              ),
            )
          : {},
    };
  } catch {
    return { ...EMPTY, loose: {} };
  }
}

export function saveDraft(draft: CollectDraft, scope: string | null = currentScope): boolean {
  return writeStored(
    sessionStorageOf(),
    scopedKeyFor(DRAFT_BASE, scope),
    JSON.stringify(draft),
  );
}

export function clearDraft(scope: string | null = currentScope): void {
  removeStored(sessionStorageOf(), scopedKeyFor(DRAFT_BASE, scope));
}

export function draftCount(draft: CollectDraft): { boxes: number; sticks: number } {
  return {
    boxes: draft.boxRefs.length,
    sticks: Object.values(draft.loose).reduce((n, v) => n + v, 0),
  };
}

/* ---- submission intent -------------------------------------------------- */

function intentKeyFor(scope: string | null): string {
  return scopedKeyFor(INTENT_BASE, scope);
}

function intentStubKeyFor(scope: string | null): string {
  return `${intentKeyFor(scope)}${INTENT_STUB_SUFFIX}`;
}

function parseIntent(raw: string | null): CollectIntent | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw) as Partial<CollectIntent>;
  if (typeof parsed.key !== 'string' || !parsed.key) return null;
  const body =
    parsed.body && typeof parsed.body === 'object' && Array.isArray(parsed.body.items)
      ? (parsed.body as SubmitPickupRequestBody)
      : null;
  return {
    key: parsed.key,
    body,
    state: parsed.state === 'uncertain' ? 'uncertain' : 'submitting',
    createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString(),
    lastAttemptAt: typeof parsed.lastAttemptAt === 'string' ? parsed.lastAttemptAt : undefined,
  };
}

/**
 * Load the pending intent. Age never deletes it — an unresolved handle must
 * not silently become an accidental replacement; it is reconciled against the
 * authoritative intent-status endpoint instead. A corrupt payload falls back
 * to the key stub (degraded probe-only handle); a corrupt stub yields null.
 */
export function loadIntent(scope: string | null = currentScope): CollectIntent | null {
  const raw = readStored(localStorageOf(), intentKeyFor(scope));
  if (raw) {
    try {
      const intent = parseIntent(raw);
      if (intent) return intent;
    } catch {
      /* fall through to stub */
    }
  }
  const stub = readStored(localStorageOf(), intentStubKeyFor(scope));
  if (!stub) return null;
  try {
    const parsed = JSON.parse(stub) as { key?: string; createdAt?: string };
    if (typeof parsed.key !== 'string' || !parsed.key) return null;
    return {
      key: parsed.key,
      body: null,
      state: 'uncertain',
      createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString(),
    };
  } catch {
    removeStored(localStorageOf(), intentStubKeyFor(scope));
    return null;
  }
}

/** F1.3 — true when storage holds a 'submitting' intent under an owner scope
 * OTHER than the active one. Such a record exists only when an owner flip
 * interrupted an in-flight submit; the FIN-01 remount that follows destroys
 * the page (and its error state) before the dying continuation's setError can
 * paint, so the remounted collect page re-derives the honest "account
 * changed" notice from this durable evidence. Presence/state only — the
 * foreign payload is never read into the UI. Covers the memory fallback too:
 * a scopeless key is NOSCOPE-namespaced and never foreign-owned. */
export function foreignSubmittingIntent(scope: string | null = currentScope): boolean {
  const prefix = `${INTENT_BASE}.`;
  const check = (key: string, raw: string | null): boolean => {
    if (!key.startsWith(prefix) || key.endsWith(INTENT_STUB_SUFFIX)) return false;
    const recScope = key.slice(prefix.length);
    if (!recScope || recScope === scope || recScope === NOSCOPE) return false;
    try {
      return parseIntent(raw)?.state === 'submitting';
    } catch {
      return false;
    }
  };
  const storage = localStorageOf();
  if (storage) {
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && check(key, storage.getItem(key))) return true;
      }
    } catch {
      /* fall through to memory */
    }
  }
  for (const [key, raw] of memStore) {
    if (check(key, raw)) return true;
  }
  return false;
}

/** Durable-layer read — the memory fallback is bypassed so callers can prove
 * what a reload would actually recover. Scopeless keys never persist. */
function readPersisted(storage: Storage | null, key: string): string | null {
  if (isScopeless(key) || !storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** F1.3 — how the durable record under `scope` relates to the intent about
 * to be dispatched:
 * - 'match':    full payload bound to this exact key + canonical body.
 * - 'stub':     probe-only recovery handle bound to this exact key (payload
 *               absent/corrupt or body unusable — never replayable alone).
 * - 'conflict': the durable slot is occupied by a DIFFERENT key, or by bytes
 *               that cannot be identified — possibly an unresolved submission
 *               still owed reconciliation. durableKey preserves what is known.
 * - 'none':     no durable record at all.
 * Presence alone is never dispatch authorization — only 'match' or 'stub'
 * prove the recovery record belongs to the intent being sent. */
export type DurableIntentBinding =
  | { kind: 'match'; intent: CollectIntent }
  | { kind: 'stub' }
  | { kind: 'conflict'; durableKey: string | null }
  | { kind: 'none' };

/** Identity-aware durable read (memory bypassed — F1.2 reload boundary +
 * F1.3 exact binding): what a reload would recover must belong to THIS
 * intent before it may authorize a dispatch. */
export function persistedIntentBinding(
  expected: CollectIntent,
  scope: string | null = currentScope,
): DurableIntentBinding {
  const store = localStorageOf();
  if (!store || !scope) return { kind: 'none' };
  const payloadRaw = readPersisted(store, intentKeyFor(scope));
  let unidentifiable = false;
  if (payloadRaw) {
    let durable: CollectIntent | null = null;
    try {
      durable = parseIntent(payloadRaw);
    } catch {
      /* corrupt bytes — unidentifiable, handled below */
    }
    if (durable) {
      if (durable.key !== expected.key) {
        return { kind: 'conflict', durableKey: durable.key };
      }
      if (durable.body) {
        // Same key: only a canonically identical body proves this record
        // belongs to the intent being dispatched — a body mismatch is a
        // contradiction that must be reconciled, not a replayable record.
        return expected.body && intentBodyEquals(durable.body, expected.body)
          ? { kind: 'match', intent: durable }
          : { kind: 'conflict', durableKey: durable.key };
      }
      // Parsed record without a usable body degrades to stub level for the
      // same key — probe-only, never a full replayable record.
      return { kind: 'stub' };
    }
    unidentifiable = true;
  }
  const stubRaw = readPersisted(store, intentStubKeyFor(scope));
  if (stubRaw) {
    let key: unknown;
    try {
      key = (JSON.parse(stubRaw) as { key?: unknown }).key;
    } catch {
      return { kind: 'conflict', durableKey: null };
    }
    // Only a valid, nonempty key matching the expected intent authorizes a
    // stub-level dispatch; anything else is an unresolved foreign record.
    if (typeof key === 'string' && key) {
      return key === expected.key ? { kind: 'stub' } : { kind: 'conflict', durableKey: key };
    }
    return { kind: 'conflict', durableKey: null };
  }
  return unidentifiable ? { kind: 'conflict', durableKey: null } : { kind: 'none' };
}

/** Durable-only intent load (no memory fallback, no mutation): exactly what
 * a reload would recover — used to reconcile a conflicting durable record
 * through the authoritative probe/abandon flow. */
export function loadPersistedIntent(scope: string | null = currentScope): CollectIntent | null {
  const store = localStorageOf();
  if (!store || !scope) return null;
  const payloadRaw = readPersisted(store, intentKeyFor(scope));
  if (payloadRaw) {
    try {
      const intent = parseIntent(payloadRaw);
      if (intent) return intent;
    } catch {
      /* fall through to the stub */
    }
  }
  const stubRaw = readPersisted(store, intentStubKeyFor(scope));
  if (!stubRaw) return null;
  try {
    const parsed = JSON.parse(stubRaw) as { key?: string; createdAt?: string };
    if (typeof parsed.key !== 'string' || !parsed.key) return null;
    return {
      key: parsed.key,
      body: null,
      state: 'uncertain',
      createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/** Persist the intent plus its key stub under the given owner scope.
 * Returns false when persistence failed (storage disabled/quota) and the
 * handle is memory-only — callers must surface that so a reload is never
 * mistaken for a safe boundary.
 * F1.3: a durable record bound to a DIFFERENT key (or unidentifiable bytes)
 * is never silently overwritten — it may be an unresolved submission still
 * owed reconciliation. The handle stays in memory and reports failure so
 * the caller can resolve the conflict through the authoritative flow. */
export function saveIntent(
  intent: CollectIntent,
  scope: string | null = currentScope,
): boolean {
  if (persistedIntentBinding(intent, scope).kind === 'conflict') {
    memStore.set(intentKeyFor(scope), JSON.stringify(intent));
    memStore.set(
      intentStubKeyFor(scope),
      JSON.stringify({ key: intent.key, createdAt: intent.createdAt }),
    );
    return false;
  }
  const persisted = writeStored(
    localStorageOf(),
    intentKeyFor(scope),
    JSON.stringify(intent),
  );
  writeStored(
    localStorageOf(),
    intentStubKeyFor(scope),
    JSON.stringify({ key: intent.key, createdAt: intent.createdAt }),
  );
  return persisted;
}

export function clearIntent(scope: string | null = currentScope): void {
  removeStored(localStorageOf(), intentKeyFor(scope));
  removeStored(localStorageOf(), intentStubKeyFor(scope));
}

export function markIntentUncertain(
  scope: string | null = currentScope,
  intent?: CollectIntent,
): void {
  const target = intent ?? loadIntent(scope);
  if (!target || target.state === 'uncertain') return;
  saveIntent({ ...target, state: 'uncertain' }, scope);
}

/** Stamp the dispatch time of a POST attempt — the quiesce signal that makes
 * an ABSENT probe meaningful (an attempt this old could not still be in
 * transit to the server marker write). */
export function touchIntentAttempt(
  intent: CollectIntent,
  scope: string | null = currentScope,
): CollectIntent {
  const stamped = { ...intent, lastAttemptAt: new Date().toISOString() };
  saveIntent(stamped, scope);
  return stamped;
}

/* F1.3 dispatch-binding control-flow signals (never API codes): a suspended
 * continuation raises these when the context captured at submit start no
 * longer holds — the session owner changed across an async boundary, or the
 * durable record no longer binds to the intent about to be dispatched. */
export class PortalOwnerChangedError extends Error {
  constructor() {
    super('PORTAL_OWNER_CHANGED');
    this.name = 'PortalOwnerChangedError';
  }
}

export class PortalIntentNotDurableError extends Error {
  readonly binding: 'none' | 'conflict';

  constructor(binding: 'none' | 'conflict') {
    super(`PORTAL_INTENT_NOT_DURABLE_${binding.toUpperCase()}`);
    this.name = 'PortalIntentNotDurableError';
    this.binding = binding;
  }
}

/** Canonical body comparison — item order and whitespace must not create a
 * false "changed payload" that silently issues a new idempotency key. A
 * degraded intent (body: null) never equals a live body. */
export function intentBodyEquals(
  a: SubmitPickupRequestBody | null,
  b: SubmitPickupRequestBody,
): boolean {
  if (!a) return false;
  const items = (body: SubmitPickupRequestBody) =>
    body.items
      .map((i) => ({
        kind: i.kind,
        boxRef: i.kind === 'BOX' ? (i.boxRef ?? '') : '',
        productId: i.kind === 'LOOSE' ? i.productId : '',
        sticks: i.kind === 'LOOSE' ? i.sticks : 0,
      }))
      .sort((x, y) =>
        `${x.kind}:${x.boxRef}:${x.productId}:${x.sticks}`.localeCompare(
          `${y.kind}:${y.boxRef}:${y.productId}:${y.sticks}`,
        ),
      );
  return (
    a.pickupShopId === b.pickupShopId &&
    a.preferredPickupDate === b.preferredPickupDate &&
    (a.customerNote ?? '').trim() === (b.customerNote ?? '').trim() &&
    JSON.stringify(items(a)) === JSON.stringify(items(b))
  );
}
